import { useKestrelViewerContext } from 'features/kestrelViewer'
import { Ruler, Thermometer, X } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const formatInchesToFeetAndInches = (totalInches: number): string => {
  const feet = Math.floor(totalInches / 12)
  const inches = totalInches % 12
  return `${feet}' ${inches.toFixed(1)}"`
}

const formatValueForLayer = (value: number, layerPrefix: string): string => {
  if (layerPrefix.startsWith('Elevation')) {
    return formatInchesToFeetAndInches(value)
  }
  return `${value.toFixed(1)}°F`
}

const TemperatureOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  gap: 0.75rem;
  min-width: 200px;
  animation: ${({ $isVisible }) =>
    $isVisible ? 'fadeIn 0.3s ease-out' : 'fadeOut 0.3s ease-out'};

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    to {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
  }
`

const TemperatureContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const TemperatureValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`

export const TemperatureDisplay: React.FC = () => {
  const {
    currentTemperature,
    setCurrentTemperature,
    selectedMode,
    temperatureProbeLoading,
    activeLayer,
  } = useKestrelViewerContext()

  const isVisible =
    selectedMode === 'temperature' &&
    (currentTemperature !== null || temperatureProbeLoading)

  const handleClose = () => {
    setCurrentTemperature(null)
  }

  const isElevationLayer = activeLayer.prefix.startsWith('Elevation')
  const Icon = isElevationLayer ? Ruler : Thermometer

  if (!isVisible) {
    return null
  }

  return (
    <TemperatureOverlay $isVisible={isVisible}>
      <Icon size={20} />
      <TemperatureContent>
        {temperatureProbeLoading ? (
          <TemperatureValue>Probing...</TemperatureValue>
        ) : currentTemperature ? (
          <TemperatureValue>
            {formatValueForLayer(
              currentTemperature.temperature,
              activeLayer.prefix
            )}
          </TemperatureValue>
        ) : null}
      </TemperatureContent>
      {!temperatureProbeLoading && (
        <CloseButton onClick={handleClose} title='Close temperature display'>
          <X size={16} />
        </CloseButton>
      )}
    </TemperatureOverlay>
  )
}
